import { icon } from 'leaflet';
import { type ProjectHierarchyPartialFragment } from '@gql/graphql';
import { type Navigation, type NavigationItem } from '@models/Navigation';
import { type Maybe } from '@models/Maybe';

export const NavigationPath = {
    PortalDashboard: '/',
    Projects: '/projects',
    SecurityNotifications: '/security-notifications',
    Tasks: '/tasks',
    NotificationComponents: '/notification-components',
    Standards: '/standards',
    Requirements: '/requirements',
    Admin: '/admin',
    CurrentUserProfile: '/me',

    // Admin menu paths
    AdminUsers: '/admin/users',
    AdminStandards: '/admin/standards',
    AdminRequirements: '/admin/requirements',
    AdminMappings: '/admin/mappings',
    AdminPss: '/admin/pss',
    AdminSupplierAssessments: '/admin/supplier-assessments',
    AdminProjectGuidance: '/admin/project-guidance',
} as const;

export const ProjectPath = {
    Dashboard: '',
    Guidance: '/guidance',
    Pss: '/pss',
    Tra: '/tra',
    SecurityNotifications: '/vulnerability/security-notifications',
    Vulnerabilities: '/vulnerability/security-notifications/vulnerabilities',
    Measures: '/vulnerability/measures',
    SystemsAndAssets: '/systems-and-assets',
    Assets: '/security-zones-assets',
    Components: '/components',
    Settings: '/settings',
    StandardProfile: '/standard-profile',
    ZoneAssets: '/zone-assets',
    Vulnerability: '/vulnerability',
    Mitigation: '/mitigation',
    Cra: '/cra',
} as const;

export function getProjectPath(
    segment: (typeof ProjectPath)[keyof typeof ProjectPath],
    pid: string,
): string {
    return `${NavigationPath.Projects}/${pid}${segment}`;
}

interface SystemsMenuParams {
    pid: string;
    systems: ProjectHierarchyPartialFragment[];
}

function getSystemsMenu({
    pid,
    systems,
}: SystemsMenuParams): Maybe<Navigation> {
    if (!systems.length) return null;

    const systemsBasePath = getProjectPath(ProjectPath.SystemsAndAssets, pid);

    const buildSystemItems = (parentId?: string): NavigationItem[] =>
        systems
            .filter((system) => system.parentHierarchy?.id === parentId)
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((system) => {
                const hasChildren = systems.some(
                    (child) => child.parentHierarchy?.id === system.id,
                );

                return {
                    label: system.name,
                    to: `${systemsBasePath}/${system.id}`,
                    subMenu: hasChildren
                        ? {
                              id: `system-${system.id}`,
                              items: buildSystemItems(system.id),
                          }
                        : undefined,
                };
            });

    return {
        id: 'systems',
        items: buildSystemItems(undefined),
    };
}

function getAdminMenu(): Navigation {
    return {
        id: 'admin',
        items: [
            {
                label: 'Users',
                to: NavigationPath.AdminUsers,
                icon: 'group',
                admin: true,
            },
            {
                label: 'Standards',
                to: NavigationPath.AdminStandards,
                icon: 'library_books',
                admin: true,
            },
            {
                label: 'Requirements',
                to: NavigationPath.AdminRequirements,
                icon: 'library_books',
                admin: true,
            },
            {
                label: 'Mappings',
                to: NavigationPath.AdminMappings,
                icon: 'beenhere',
                admin: true,
            },
            {
                label: 'PSSP classification templates',
                to: NavigationPath.AdminPss,
                icon: 'book',
                admin: true,
            },
            {
                label: '3rd party Supplier Assessments',
                to: NavigationPath.AdminSupplierAssessments,
                icon: 'book',
                admin: true,
            },
            {
                label: 'Project Guidance Configuration',
                to: NavigationPath.AdminProjectGuidance,
                icon: 'flowchart',
                admin: true,
            },
        ],
    };
}

interface ProjectMenuParams {
    pid: string;
    systems: ProjectHierarchyPartialFragment[];
    craCount: number;
}

function getProjectMenu({
    pid,
    systems,
    craCount,
}: ProjectMenuParams): Maybe<Navigation> {
    if (!pid) return undefined;

    const systemsSubMenu = getSystemsMenu({ pid, systems });

    return {
        id: 'project',
        items: [
            {
                label: 'Project dashboard',
                to: getProjectPath(ProjectPath.Dashboard, pid),
                icon: 'dashboard_customize',
                exact: true,
            },
            ...(craCount
                ? [
                      {
                          label: 'CRA',
                          to: getProjectPath(ProjectPath.Cra, pid),
                          icon: 'assured_workload',
                      } as NavigationItem,
                  ]
                : []),
            {
                label: 'Project guidance',
                to: getProjectPath(ProjectPath.Guidance, pid),
                icon: 'flowchart',
            },
            {
                label: 'PSSP classification',
                to: getProjectPath(ProjectPath.Pss, pid),
                icon: 'category',
            },
            {
                label: 'Threat & Risk Analysis',
                to: getProjectPath(ProjectPath.Tra, pid),
                icon: 'health_and_safety',
                exact: false,
            },
            {
                label: 'Security notifications',
                to: getProjectPath(ProjectPath.SecurityNotifications, pid),
                icon: 'gpp_maybe',
            },
            {
                label: 'Measures & mitigations',
                to: getProjectPath(ProjectPath.Measures, pid),
                icon: 'healing',
            },
            {
                label: 'Systems & assets',
                to: getProjectPath(ProjectPath.SystemsAndAssets, pid),
                icon: 'full_stacked_bar_chart',
                subMenu: systemsSubMenu ?? undefined,
                exact: Boolean(systemsSubMenu),
            },
            {
                label: 'Zones & assets',
                to: getProjectPath(ProjectPath.Assets, pid),
                icon: 'network_locked',
                exact: false,
            },
            {
                label: 'Notification components',
                to: getProjectPath(ProjectPath.Components, pid),
                icon: 'shapes',
            },
            {
                label: 'Project settings',
                to: getProjectPath(ProjectPath.Settings, pid),
                icon: 'settings',
            },
        ],
    };
}

export function getMenuContent(
    pid?: string,
    systems: ProjectHierarchyPartialFragment[] = [],
    craCount = 0,
): Navigation {
    return {
        id: 'default',
        items: [
            {
                label: 'Portal dashboard',
                to: NavigationPath.PortalDashboard,
                icon: 'grid_view',
            },
            {
                label: 'Projects',
                to: NavigationPath.Projects,
                icon: 'dashboard',
                exact: true,
                subMenu: pid
                    ? getProjectMenu({ pid, systems, craCount })
                    : undefined,
            },
            {
                label: 'Security notifications',
                to: NavigationPath.SecurityNotifications,
                icon: 'gpp_maybe',
            },
            {
                label: 'My tasks',
                to: NavigationPath.Tasks,
                icon: 'checkbook',
            },
            {
                label: 'Notification components',
                to: NavigationPath.NotificationComponents,
                icon: 'shapes',
            },
            {
                label: 'Standards',
                to: NavigationPath.Standards,
                icon: 'library_books',
            },
            {
                label: 'Administration',
                to: NavigationPath.Admin,
                icon: 'admin_panel_settings',
                exact: true,
                admin: true,
                subMenu: getAdminMenu(),
            },
        ],
    };
}
